import { BaseTable, Button, Loading, PaginationData, PermissionLink, TableHeaderSort, TableNumber, TableSorting } from "components";
import { PicturesSection } from "pages/Inventori/Register/ItemGroup/components";
import { convertNumber } from "utils";
import { useListStockGlobal } from "../hooks/useListStockGlobal";
import { FilterTypeSetStockGlobal } from "../types/itemStockGlobal";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePurchaseFromStockStore } from "../store/usePurchaseFromStockStore";
import { PERMISION } from "security";
import iconPurchase from "../../../../../assets/images/purchase-order-report.png";
import iconBuild from "../../../../../assets/images/build-item-report.png";


export function Table({
    filter,
    setFilter,
    data,
    showMinus,
    showPlus,
}: {
    filter: FilterTypeSetStockGlobal
    setFilter: React.Dispatch<React.SetStateAction<FilterTypeSetStockGlobal>>
    data: ReturnType<typeof useListStockGlobal>
    showMinus: boolean
    showPlus: boolean
}) {
    
    const navigate = useNavigate()
    const { setItem } = usePurchaseFromStockStore()
    
    // data
    const { items } = data.data


    const header: TableSorting[] = [
        { title: "CATEGORY", sort: false, value: "category", isActive: true, align: "center" },
        { title: "PICTURE", sort: false, value: "picture", isActive: true, align: "center" },
        { title: "BSI", sort: false, value: "bsi", isActive: true },
        { title: "BUILD", sort: false, value: "build", isActive: true },
        { title: "BARCODE", sort: false, value: "barcode", isActive: true },
        { title: "PRODUCT", sort: false, value: "product", isActive: true },
        { title: "UNIT (Inventory)", className:"whitespace-normal max-w-[70px]",sort: false, value: "unit", isActive: true },
        { title: "SOH", sort: true, value: "soh", align: "right", isActive: true },
        { title: "MIN", sort: true, value: "min_qty", align: "right", isActive: true },
        { title: "MAX", sort: true, value: "max_qty", align: "right", isActive: true },
        { title: "DIV", sort: true, value: "div", align: "right", isActive: true },
    ];

    const sortMapping: Record<string, string> = {
        "SOH": "soh",
        "MIN": "min_qty",
        "MAX": "max_qty",
        "DIV": "div",
    };

    const [tableActive] = useState<TableSorting[]>(header);


    const handleSort = (item: string, sortType: string) => {
        const sortParam = sortMapping[item]? `${sortMapping[item]}:${sortType}`
        : "";
        console.log(sortParam);
        
        setFilter({ ...filter, sort: sortParam });
    };


    const filteredItems = useMemo(() => {
        return items.filter((item) => {
            const min = Number(item.min_qty)
            const max = Number(item.max_qty)
            const soh = Number(item.soh)
        
            let div = 0
            if (soh < min) div = soh - min
            else if (soh > max) div = soh - max
        
            //  hide minus
            if (!showMinus && div < 0) return false
        
            //  hide plus
            if (!showPlus && div > 0) return false
        
            return true
        })
    }, [items, showMinus, showPlus])
      
    return (
        <div>
            <div className="overflow-x-auto max-h-[calc(100vh-600px)]">
                <BaseTable className="border-collapse">
                    <thead className="sticky top-[200px] z-[3] !bg-black !text-white">
                        <tr>
                            <td>NO</td>
                            <TableHeaderSort
                                title={tableActive}
                                onSort={(item, sortType) =>
                                    handleSort(item, sortType)
                                }
                            />
                            <td>ACTION</td>
                        </tr>
                    </thead>

                    <tbody>
                        {data.isLoading ? (
                            <tr>
                                <td colSpan={14} className="text-center">
                                    <Loading loading errorText="Loading..." />
                                </td>
                            </tr>
                        ) : filteredItems.length === 0 ? (
                            <tr>
                                <td colSpan={14} className="text-center">
                                    No data available
                                </td>
                            </tr>
                        ) : (
                            filteredItems.map((item, index: number) => {
                                let div : number = 0
                                const min = Number(item.min_qty)
                                const max = Number(item.max_qty)
                                const soh = Number(item.soh)
                                if(soh < min){
                                    div = soh - min
                                }else if(soh > max){
                                    div = soh - max
                                }

                                return (
                                    <tr key={item.product_id ?? index}>
                                        <td>
                                            {TableNumber({
                                                dataLength: items.length,
                                                pageCurrent: filter.page,
                                                index,
                                                limit: filter.limit,
                                                totalData: filter.total,
                                            })}
                                        </td>

                                        <td>{item.category_name}</td>

                                        <td className="!w-[80px]">
                                            <div className="flex justify-center">
                                                {Boolean(item?.pictures?.length) && (
                                                    <PicturesSection
                                                        dataPicture={item.pictures}
                                                        items={1}
                                                        className="size-[50px] my-[-10px]"
                                                    />
                                                )}
                                            </div>
                                        </td>

                                        <td className="text-center">
                                            {`${item.buy ? "B" : ""}${item.sell ? "S" : ""}${item.inventory ? "I" : ""}`}
                                        </td>

                                        <td className="text-center">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-primary"
                                                checked={item.build_item}
                                                disabled
                                            />
                                        </td>

                                        <td>{item.product_barcode}</td>
                                        <td>{item.product_name}</td>
                                        <td>{item.mr_unit_name}</td>

                                        <td className="text-right">
                                            {convertNumber(Number(item.soh) ?? 0).intoCurrency}
                                        </td>

                                        <td className="text-right">
                                            {convertNumber(Number(item.min_qty) ?? 0).intoCurrency}
                                        </td>

                                        <td className="text-right">
                                            {convertNumber(Number(item.max_qty) ?? 0).intoCurrency}
                                        </td>

                                        <td className={`text-right ${div < 0 ? "text-error" : div > 0 ? 'text-success' :''}`}>
                                            {div !== 0 ? convertNumber(div).intoCurrency : ''}
                                        </td>

                                        <td>
                                            {item.buy && (
                                            // <PermissionLink
                                            //     permission="IT162"
                                            //     to={`/purchase/transaction/purchase-order`}
                                            // >
                                            //     <Button color="primary">PO</Button>
                                            // </PermissionLink>
                                                <Button
                                                    color="primary"
                                                    permission={PERMISION.INVENTORY.REPORT.ITEM_DETAIL_STOCK_GLOBAL.UPDATE}
                                                    onClick={() => {
                                                        const qty_order = getQtyOrderFromStock(
                                                            Number(item.soh),
                                                            Number(item.min_qty),
                                                            Number(item.max_qty),
                                                            Number(item.quantity_unit_convention)
                                                        )
                                                        console.log(item);
                                                        
                                                        setItem({
                                                            product_id: item.product_id?.toString() ?? "",
                                                            product_name: item.product_name,
                                                            qty_order,
                                                            price_measure:Number(item.buying_price),
                                                            mr_unit_id:item.weight_unit_id.toString()
                                                          })

                                                        navigate("/purchase/transaction/purchase-order")
                                                    }}
                                                    >
                                                  <img src={iconPurchase} width={30} height={30} alt="po" />
                                                </Button>
                                            )}

                                            {!item.buy && item.build_item && (
                                            <PermissionLink
                                                permission={PERMISION.INVENTORY.REPORT.ITEM_DETAIL_STOCK_GLOBAL.UPDATE}
                                                to={`/inventory/transaction/build-item/${item.product_id}`}
                                            >
                                                <Button color="primary">
                                                     <img src={iconBuild} width={30} height={30} alt="build" />
                                                </Button>
                                            </PermissionLink>
                                            )}
                                        </td>

                                    </tr>
                                )
                            })
                        )}

                    </tbody>
                </BaseTable>
            </div>
            <div className="mt-5">
                <PaginationData
                    data={data.data}
                    pagination={filter}
                    setPagination={setFilter}
                />
            </div>
        </div>
    )
}

  
const getQtyOrderFromStock = (
    soh: number,
    min: number,
    max: number,
    quantity_unit_convention:number|null
): number => {

    let qty = max - soh
    if (qty < 0) {
        qty = max
    }

    let convertion_qty = qty / Number(quantity_unit_convention ?? 1)
    return convertion_qty
}
